import React, { useState } from 'react';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';

// ============================================================
// Shared layout for every country visa page (Oman, UAE, KSA,
// Bahrain, Kuwait, Qatar). Each page passes in its own content
// through props — this file only owns structure and styling,
// so the visual identity stays identical across all six.
// ============================================================

export interface VisaPlan {
  id: string;
  name: string;
  entryType: string;
  validity: string;
  stay: string;
  processing: string;
  price: string;
  currency: string;
  featured?: boolean;
}

export interface RequirementItem {
  label: string;
  detail: string;
}

export interface ProcessStep {
  title: string;
  text: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface VisaCategory {
  icon: React.ReactNode;
  title: string;
  text: string;
}

export interface VisaPageLayoutProps {
  countryName: string;
  flagEmoji: string;
  heroEyebrow: string;
  heroImage: string;
  heroTitleLead: string;
  heroTitleHighlight: string;
  heroTitleTail?: string;
  heroSubtitle: string;
  trustStats: { value: string; label: string }[];
  categories?: VisaCategory[];
  plans: VisaPlan[];
  requirements: RequirementItem[];
  goodToKnow: string[];
  steps: ProcessStep[];
  useTimeline?: boolean;
  faqs: FaqItem[];
  ctaSubtitle: string;
}

/** Dune-silhouette rule — the same recurring signature divider used
 *  across the site instead of a plain line. */
function DuneRule() {
  return (
    <div aria-hidden="true" style={{ lineHeight: 0 }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16" fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5" />
        <path d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22" fill="none" stroke="rgba(41,75,90,.14)" strokeWidth="1" />
      </svg>
    </div>
  );
}

function FaqAccordion({ faqs }: { faqs: FaqItem[] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="faq-list">
      {faqs.map((f, i) => {
        const isOpen = open === i;
        return (
          <div className={`faq-item${isOpen ? " is-open" : ""}`} key={i}>
            <button className="faq-q" onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen}>
              <span>{f.question}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="faq-chevron">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {isOpen && <div className="faq-a">{f.answer}</div>}
          </div>
        );
      })}
    </div>
  );
}

export default function VisaPageLayout2({
  countryName,
  flagEmoji,
  heroEyebrow,
  heroImage,
  heroTitleLead,
  heroTitleHighlight,
  heroTitleTail,
  heroSubtitle,
  trustStats,
  categories,
  plans,
  requirements,
  goodToKnow,
  steps,
  useTimeline,
  faqs,
  ctaSubtitle,
}: VisaPageLayoutProps) {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --radius-sm: 5px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }
        * { box-sizing: border-box; }
        .visa-page { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        .visa-page h1, .visa-page h2, .visa-page h3, .visa-page h4 { font-family: 'Playfair Display', serif; }
        .visa-page a:focus-visible, .visa-page button:focus-visible { outline: 2px solid var(--gold-d); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { .visa-page *, .visa-page *::before, .visa-page *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }

        .section-title { font-size: clamp(1.7rem, 2.6vw, 2.4rem); font-weight: 500; color: var(--ink); line-height: 1.2; letter-spacing: -.01em; margin: 10px 0 12px; }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 520px; }
        .section-pad { padding: 84px 0; }
        @media (max-width: 991px) { .section-pad { padding: 64px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 48px 0; } }
        .bg-cream { background: var(--cream); }
        .bg-white { background: #fff; }

        /* HERO */
        .visa-hero { position: relative; min-height: 420px; display: flex; align-items: center; overflow: hidden; background: var(--teal-d); }
        .visa-hero-img { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .38; }
        .visa-hero-veil { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(26,49,61,.55) 0%, rgba(26,49,61,.92) 100%); }
        .visa-hero-inner { position: relative; z-index: 2; padding: 64px 0 56px; }
        .visa-breadcrumb { font-size: 12px; color: rgba(255,255,255,.55); margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }
        .visa-breadcrumb a { color: rgba(255,255,255,.55); text-decoration: none; }
        .visa-breadcrumb a:hover { color: var(--gold); }
        .visa-flag { font-size: 34px; line-height: 1; margin-bottom: 14px; display: block; }
        .visa-hero-title { font-weight: 600; color: #fff; line-height: 1.16; font-size: clamp(30px, 4vw, 48px); max-width: 640px; margin-bottom: 16px; }
        .visa-hero-title span.gold { font-style: italic; color: var(--gold); font-weight: 400; }
        .visa-hero-sub { font-size: 15px; color: rgba(255,255,255,.78); font-weight: 300; line-height: 1.75; max-width: 540px; margin-bottom: 30px; }
        .visa-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 34px; }
        .vbtn-primary { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--teal-d); border-radius: 5px; font-size: 12.5px; font-weight: 700; letter-spacing: .3px; padding: 14px 26px; text-decoration: none; transition: transform .2s, background .2s; box-shadow: 0 10px 28px rgba(211,190,118,.28); }
        .vbtn-primary:hover { background: #e2ce86; transform: translateY(-2px); }
        .vbtn-ghost { display: inline-flex; align-items: center; background: rgba(255,255,255,.1); backdrop-filter: blur(4px); color: #fff; border: 1px solid rgba(255,255,255,.25); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 14px 24px; text-decoration: none; transition: background .2s, border-color .2s; }
        .vbtn-ghost:hover { border-color: #fff; background: rgba(255,255,255,.18); }

        .hero-stats-row { display: flex; gap: 0; flex-wrap: wrap; border-top: 1px solid rgba(255,255,255,.14); padding-top: 24px; }
        .hero-stat { padding: 0 26px 0 0; margin-right: 26px; border-right: 1px solid rgba(255,255,255,.14); }
        .hero-stat:last-child { border-right: none; }
        .hero-stat-num { font-size: 1.5rem; font-weight: 600; color: var(--gold); line-height: 1; margin-bottom: 5px; display: block; }
        .hero-stat-label { font-size: 10.5px; color: rgba(255,255,255,.55); letter-spacing: .4px; }
        @media (max-width: 640px) { .hero-stats-row { gap: 18px 0; } .hero-stat { border-right: none; margin-right: 18px; padding-right: 0; } }

        /* VISA PLAN CARDS — boarding pass style, consistent with the homepage */
        .visa-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 26px; }
        @media (max-width: 991px) { .visa-grid { grid-template-columns: 1fr; max-width: 420px; margin: 0 auto; } }
        .visa-card { border-radius: var(--radius); border: 1px solid rgba(41,75,90,.1); background: #fff; position: relative; transition: transform .3s, box-shadow .3s, border-color .3s; }
        .visa-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); border-color: rgba(211,190,118,.4); }
        .visa-card.feat { border-color: var(--gold); }
        .visa-badge { position: absolute; top: -1px; right: 22px; background: var(--gold); color: var(--teal-d); font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 6px 14px; border-radius: 0 0 5px 5px; }
        .visa-head { padding: 28px 26px 20px; }
        .visa-name { font-size: 1.2rem; font-weight: 600; color: var(--ink); line-height: 1.25; margin-bottom: 8px; }
        .visa-type { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--gold-d); }
        .visa-price-row { margin-top: 16px; display: flex; align-items: baseline; gap: 6px; }
        .visa-price-num { font-size: 1.7rem; font-weight: 600; color: var(--teal); }
        .visa-price-cur { font-size: 11px; color: var(--silver); letter-spacing: .5px; }
        .visa-perforation { position: relative; height: 0; border-top: 2px dashed var(--mist); margin: 0 26px; }
        .visa-perforation::before, .visa-perforation::after { content: ''; position: absolute; top: -11px; width: 22px; height: 22px; border-radius: 50%; background: var(--cream); }
        .visa-perforation::before { left: -37px; }
        .visa-perforation::after { right: -37px; }
        .visa-body { padding: 20px 26px 26px; }
        .visa-row { display: flex; align-items: flex-start; gap: 12px; padding: 10px 0; border-bottom: 1px solid rgba(41,75,90,.06); font-size: 13px; }
        .visa-row:last-of-type { border-bottom: none; }
        .visa-check { width: 20px; height: 20px; border-radius: 50%; background: rgba(211,190,118,.15); display: flex; align-items: center; justify-content: center; font-size: 9px; color: var(--gold-d); flex-shrink: 0; margin-top: 1px; }
        .visa-label { color: var(--silver); font-weight: 300; line-height: 1.5; }
        .visa-label strong { display: block; color: var(--ink); font-weight: 500; font-size: 12.5px; margin-bottom: 1px; }
        .visa-apply { display: block; width: 100%; margin-top: 20px; background: var(--teal); color: #fff; border: none; border-radius: var(--radius-sm); font-size: 11.5px; font-weight: 700; letter-spacing: .4px; padding: 15px 20px; cursor: pointer; text-align: center; text-decoration: none; transition: background .2s, transform .2s; }
        .visa-apply:hover { background: var(--teal-d); transform: translateY(-1px); }
        .visa-apply.feat-btn { background: var(--gold); color: var(--teal-d); }
        .visa-apply.feat-btn:hover { background: var(--gold-d); }

        /* REQUIREMENTS */
        .req-layout { display: grid; grid-template-columns: 1.3fr 1fr; gap: 40px; align-items: start; }
        @media (max-width: 900px) { .req-layout { grid-template-columns: 1fr; } }
        .req-list { display: flex; flex-direction: column; gap: 0; background: #fff; border: 1px solid rgba(41,75,90,.09); border-radius: var(--radius); overflow: hidden; }
        .req-item { display: flex; gap: 16px; padding: 20px 24px; border-bottom: 1px solid rgba(41,75,90,.07); }
        .req-item:last-child { border-bottom: none; }
        .req-num { width: 30px; height: 30px; border-radius: 50%; background: var(--cream); border: 1px solid var(--gold); color: var(--teal); font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .req-label { font-size: 14px; font-weight: 600; color: var(--ink); margin-bottom: 3px; }
        .req-detail { font-size: 13px; color: var(--silver); font-weight: 300; line-height: 1.6; }
        .info-card { background: var(--teal-d); border-radius: var(--radius); padding: 30px 28px; color: #fff; }
        .info-card-title { font-size: 1.05rem; font-weight: 500; margin-bottom: 16px; color: var(--gold-l); }
        .info-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 13px; }
        .info-list li { display: flex; gap: 10px; font-size: 13px; color: rgba(255,255,255,.8); font-weight: 300; line-height: 1.6; }
        .info-list li::before { content: '—'; color: var(--gold); flex-shrink: 0; }

        /* PROCESS — flight route, consistent with the homepage */
        .route-wrap { position: relative; padding-top: 8px; }
        .route-line { position: absolute; top: 29px; left: 6%; right: 6%; height: 0; border-top: 2px dashed rgba(211,190,118,.6); z-index: 0; }
        .how-grid { display: grid; grid-template-columns: repeat(4, 1fr); position: relative; z-index: 1; }
        @media (max-width: 768px) { .how-grid { grid-template-columns: repeat(2, 1fr); gap: 40px 20px; } .route-line { display: none; } }
        .how-item { text-align: center; padding: 0 18px; }
        .how-num-ring { width: 54px; height: 54px; border-radius: 50%; border: 1px solid var(--gold); display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; background: #fff; box-shadow: 0 0 0 6px var(--cream); }
        .how-num { font-size: 1.2rem; font-weight: 600; color: var(--teal); }
        .how-title { font-size: 14px; font-weight: 600; color: var(--ink); margin-bottom: 8px; }
        .how-text { font-size: 12.5px; color: var(--silver); font-weight: 300; line-height: 1.65; max-width: 200px; margin: 0 auto; }

        /* FAQ */
        .faq-list { max-width: 760px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }
        .faq-item { background: #fff; border: 1px solid rgba(41,75,90,.09); border-radius: var(--radius); overflow: hidden; transition: border-color .2s; }
        .faq-item.is-open { border-color: rgba(211,190,118,.45); }
        .faq-q { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; background: none; border: none; cursor: pointer; text-align: left; padding: 18px 22px; font-size: 14.5px; font-weight: 600; color: var(--ink); }
        .faq-chevron { color: var(--gold-d); flex-shrink: 0; transition: transform .25s; }
        .faq-item.is-open .faq-chevron { transform: rotate(180deg); }
        .faq-a { padding: 0 22px 20px; font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.75; }

        /* CTA BANNER */
        .cta-banner { background: var(--teal); border-radius: var(--radius); padding: 52px 48px; display: flex; align-items: center; justify-content: space-between; gap: 30px; flex-wrap: wrap; }
        .cta-banner-title { color: #fff; font-size: 1.5rem; font-weight: 500; margin-bottom: 8px; }
        .cta-banner-sub { color: rgba(255,255,255,.7); font-size: 13.5px; font-weight: 300; max-width: 460px; }
        /* VISA CATEGORIES — "which visa do you need" picker */
        .cat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        @media (max-width: 900px) { .cat-grid { grid-template-columns: 1fr; } }
        .cat-card { background: #fff; border: 1px solid rgba(41,75,90,.09); border-radius: var(--radius); padding: 28px 26px; transition: transform .3s, box-shadow .3s, border-color .3s; }
        .cat-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-sm); border-color: rgba(211,190,118,.35); }
        .cat-icon { color: var(--gold-d); margin-bottom: 16px; display: block; }
        .cat-title { font-size: 1.05rem; font-weight: 500; color: var(--teal); margin-bottom: 8px; }
        .cat-text { font-size: 13px; color: var(--silver); line-height: 1.65; font-weight: 300; }

        /* VERTICAL APPLICATION-JOURNEY TIMELINE — for longer, appointment-based visas */
        .timeline { position: relative; max-width: 760px; margin: 0 auto; }
        .timeline::before { content: ''; position: absolute; top: 6px; bottom: 6px; left: 23px; width: 2px; background: repeating-linear-gradient(0deg, var(--gold) 0 6px, transparent 6px 12px); }
        @media (min-width: 769px) { .timeline::before { left: 50%; margin-left: -1px; } }
        .tl-item { position: relative; padding-left: 62px; margin-bottom: 40px; }
        .tl-item:last-child { margin-bottom: 0; }
        .tl-node { position: absolute; left: 0; top: 0; width: 48px; height: 48px; border-radius: 50%; background: #fff; border: 1px solid var(--gold); display: flex; align-items: center; justify-content: center; font-size: 1rem; font-weight: 600; color: var(--teal); box-shadow: 0 0 0 6px var(--cream); z-index: 1; }
        .tl-card { background: #fff; border: 1px solid rgba(41,75,90,.09); border-radius: var(--radius); padding: 22px 24px; }
        .tl-title { font-size: 1rem; font-weight: 600; color: var(--ink); margin-bottom: 6px; }
        .tl-text { font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.7; }
        @media (min-width: 769px) {
          .tl-item { width: 50%; padding-left: 0; }
          .tl-item:nth-child(odd) { padding-right: 44px; text-align: right; }
          .tl-item:nth-child(even) { padding-left: 44px; margin-left: 50%; }
          .tl-node { left: auto; }
          .tl-item:nth-child(odd) .tl-node { right: -24px; }
          .tl-item:nth-child(even) .tl-node { left: -24px; }
        }
      `}</style>

      <div className="visa-page">
        <Header />

        {/* HERO */}
        <section className="visa-hero">
          <div className="visa-hero-img" style={{ backgroundImage: `url(${heroImage})` }} />
          <div className="visa-hero-veil" />
          <div className="container visa-hero-inner">
            <div className="visa-breadcrumb">
              <a href="/">Home</a><span>/</span><a href="/visa">Visas</a><span>/</span><span style={{ color: "#fff" }}>{countryName}</span>
            </div>
            <span className="visa-flag">{flagEmoji}</span>
            <div className="eyebrow light" style={{ marginBottom: 14 }}>{heroEyebrow}</div>
            <h1 className="visa-hero-title">
              {heroTitleLead} <span className="gold">{heroTitleHighlight}</span> {heroTitleTail || ""}
            </h1>
            <p className="visa-hero-sub">{heroSubtitle}</p>
            <div className="visa-hero-ctas">
              <a href="#plans" className="vbtn-primary">
                See visa plans
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </a>
              <a href="/contact" className="vbtn-ghost">Ask a question</a>
            </div>
            <div className="hero-stats-row">
              {trustStats.map((s, i) => (
                <div className="hero-stat" key={i}>
                  <span className="hero-stat-num">{s.value}</span>
                  <span className="hero-stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CATEGORIES — which visa do you need */}
        {categories && categories.length > 0 && (
          <section className="section-pad bg-cream" style={{ paddingBottom: 0 }}>
            <div className="container">
              <div className="text-center" style={{ marginBottom: 40 }}>
                <span className="eyebrow" style={{ justifyContent: "center" }}>Start Here</span>
                <h2 className="section-title">Which visa do you <em style={{ fontStyle: "italic", color: "var(--teal-l)" }}>need?</em></h2>
                <p className="section-sub" style={{ margin: "0 auto" }}>Every trip is different — pick the category closest to yours and we'll confirm the details.</p>
              </div>
              <div className="cat-grid">
                {categories.map((c, i) => (
                  <div className="cat-card" key={i}>
                    <span className="cat-icon">{c.icon}</span>
                    <div className="cat-title">{c.title}</div>
                    <p className="cat-text">{c.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* VISA PLANS */}
        <section className="section-pad bg-white" id="plans">
          <div className="container">
            <div className="text-center" style={{ marginBottom: 48 }}>
              <span className="eyebrow" style={{ justifyContent: "center" }}>Transparent Pricing</span>
              <h2 className="section-title">{countryName} visa <em style={{ fontStyle: "italic", color: "var(--teal-l)" }}>plans</em></h2>
              <p className="section-sub" style={{ margin: "0 auto" }}>Choose the entry type and duration that fits your trip. Fees below include our service charge — no hidden costs.</p>
            </div>
            <div className="visa-grid">
              {plans.map((plan) => (
                <div className={`visa-card ${plan.featured ? "feat" : ""}`} key={plan.id}>
                  {plan.featured && <div className="visa-badge">Most Popular</div>}
                  <div className="visa-head">
                    <div className="visa-name">{plan.name}</div>
                    <div className="visa-type">{plan.entryType}</div>
                    <div className="visa-price-row">
                      <span className="visa-price-num">{plan.currency} {plan.price}</span>
                    </div>
                  </div>
                  <div className="visa-perforation" />
                  <div className="visa-body">
                    <div className="visa-row"><div className="visa-check">✓</div><div className="visa-label"><strong>Processing time</strong>{plan.processing}</div></div>
                    <div className="visa-row"><div className="visa-check">✓</div><div className="visa-label"><strong>Validity</strong>{plan.validity}</div></div>
                    <div className="visa-row"><div className="visa-check">✓</div><div className="visa-label"><strong>Permitted stay</strong>{plan.stay}</div></div>
                    <a href="/apply" className={`visa-apply ${plan.featured ? "feat-btn" : ""}`}>Apply Now</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* REQUIREMENTS */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow">What You'll Need</span>
              <h2 className="section-title">Document <em style={{ fontStyle: "italic", color: "var(--teal-l)" }}>checklist</em></h2>
              <p className="section-sub">Have these ready before you start — it keeps processing fast and avoids back-and-forth.</p>
            </div>
            <div className="req-layout">
              <div className="req-list">
                {requirements.map((r, i) => (
                  <div className="req-item" key={i}>
                    <div className="req-num">{i + 1}</div>
                    <div>
                      <div className="req-label">{r.label}</div>
                      <div className="req-detail">{r.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="info-card">
                <div className="info-card-title">Good to know</div>
                <ul className="info-list">
                  {goodToKnow.map((g, i) => <li key={i}>{g}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* PROCESS */}
        <section className="section-pad bg-white">
          <div className="container">
            <div className="text-center" style={{ marginBottom: 52 }}>
              <span className="eyebrow" style={{ justifyContent: "center" }}>{useTimeline ? "Your Journey" : "Simple Process"}</span>
              <h2 className="section-title">How it <em style={{ fontStyle: "italic", color: "var(--teal-l)" }}>works</em></h2>
            </div>
            {useTimeline ? (
              <div className="timeline">
                {steps.map((s, i) => (
                  <div className="tl-item" key={i}>
                    <div className="tl-node">{String(i + 1).padStart(2, "0")}</div>
                    <div className="tl-card">
                      <div className="tl-title">{s.title}</div>
                      <p className="tl-text">{s.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="route-wrap">
                <div className="route-line" />
                <div className="how-grid">
                  {steps.map((s, i) => (
                    <div className="how-item" key={i}>
                      <div className="how-num-ring"><span className="how-num">{String(i + 1).padStart(2, "0")}</span></div>
                      <div className="how-title">{s.title}</div>
                      <p className="how-text">{s.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <DuneRule />

        {/* FAQ */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div className="text-center" style={{ marginBottom: 40 }}>
              <span className="eyebrow" style={{ justifyContent: "center" }}>Questions</span>
              <h2 className="section-title">Frequently <em style={{ fontStyle: "italic", color: "var(--teal-l)" }}>asked</em></h2>
            </div>
            <FaqAccordion faqs={faqs} />
          </div>
        </section>

        {/* CTA */}
        <section className="section-pad bg-white" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="cta-banner">
              <div>
                <div className="cta-banner-title">Ready to apply for your {countryName} visa?</div>
                <div className="cta-banner-sub">{ctaSubtitle}</div>
              </div>
              <a href="/apply" className="vbtn-primary">
                Start application
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </a>
            </div>
          </div>
        </section>

        <DefaultFooter />
      </div>
    </>
  );
}